import React, { useCallback, useState } from "react";

import { Heading } from "@magnetic/heading";
import { Notification } from "@magnetic/notification";
import { Spinner } from "@magnetic/spinner";
import { Text } from "@magnetic/text";

import { Button } from "src/components/buttons";

import { HFlex } from "./hFlex";
import { Modal } from "./modal";
import { VFlex } from "./vFlex";

type DeleteProps = {
  readonly title: React.ReactNode;
  readonly cancelText?: string | undefined;
  readonly deleteText?: string | undefined;
  readonly message: React.ReactNode;
  readonly show: boolean;
  readonly onDelete: () => void;
  readonly onCancel: () => void;
  readonly error?: string | React.ReactNode;
  readonly disabled?: boolean;
};

export const DeleteModal = (props: DeleteProps) => {
  const {
    show = false,
    title,
    message,
    onCancel,
    onDelete,
    error,
    cancelText = "Cancel",
    deleteText = "Delete",
    disabled = false
  } = props;
  // need to tell the difference between disabled and actively deleting.
  const [deleting, setDeleting] = useState<boolean>(false);

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      setDeleting(true);
      onDelete();
    },
    [onDelete]
  );

  const handleCancel = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    },
    [onCancel]
  );

  return (
    <Modal isOpen={show}>
      <VFlex>
        <HFlex
          align="center"
          gap="sm"
          margin={{
            bottom: 16
          }}
        >
          <Heading as="h2" size="primary">
            {title}
          </Heading>
        </HFlex>
        {error && <Notification status="negative">{error}</Notification>}
        <Text
          size="p3"
          style={{
            marginBottom: 24
          }}
        >
          {message}
        </Text>
        <HFlex justify="flex-end">
          <Button.Group>
            <Button kind="tertiary" name="modal-cancel" onClick={handleCancel}>
              {cancelText}
            </Button>
            <Button
              destructive={true}
              name="modal-remove"
              onClick={handleDelete}
              disabled={disabled}
              icon={deleting && disabled ? <Spinner size="xs" /> : null}
            >
              {deleteText}
            </Button>
          </Button.Group>
        </HFlex>
      </VFlex>
    </Modal>
  );
};
